import type { SliderProps } from "@mui/material";
import { FormControl, FormLabel, Input, Slider, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useElementTracking } from "../../../../analytics/analytics";

const renderName = (name: string): string => `table-column-popover-${name}-slider`;

export const TableColumnPopoverSlider: FC<{
  value: number;
  onChange: (value: number) => void;
  name: string;
  label: string;
  sliderProps: SliderProps;
}> = ({ value, onChange, name, label, sliderProps }) => {
  const analyticsProps = useElementTracking("email_design", name, "slider");
  const [isRangeError, setIsRangeError] = useState(false);
  const [inputValue, setInputValue] = useState(String(value));

  const handleSliderChange = useCallback(
    (e: Event, newValue: number | number[]) => {
      if (Array.isArray(newValue)) {
        return;
      }
      setInputValue(String(newValue));
      onChange(newValue);
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
      const newValue = parseInt(event.target.value);
      if (isNaN(newValue)) {
        setIsRangeError(true);
        return;
      }
      setIsRangeError(false);
      if (newValue < 0) {
        setIsRangeError(true);
        return;
      }
      if (newValue > (sliderProps.max || 50)) {
        setIsRangeError(true);
        return;
      }
      onChange(newValue);
    },
    [onChange, sliderProps],
  );

  return (
    <FormControl
      sx={{
        width: "100%",
      }}
    >
      <FormLabel>{label}</FormLabel>
      <Stack direction="row" alignItems="center">
        <Slider
          id={renderName(name)}
          aria-label={label}
          data-analytics-id={renderName(name)}
          data-test-id={`table-column-popover-${name}-slider`}
          onChange={handleSliderChange}
          value={value}
          valueLabelFormat={(val) => `${val}px`}
          valueLabelDisplay="auto"
          aria-labelledby="label-padding"
          slotProps={{
            input: analyticsProps,
          }}
          sx={{
            marginX: 2,
            flexGrow: 1,
          }}
          {...sliderProps}
        />
        <Input
          data-analytics-id={renderName(name)}
          data-test-id={`table-column-popover-${name}-input`}
          value={inputValue}
          onChange={handleInputChange}
          inputProps={{
            "aria-label": label,
            "aria-labelledby": renderName(name),
            ...analyticsProps,
          }}
          sx={{ width: 50, marginLeft: 1, marginTop: 0, marginBottom: 0 }}
          type="tel"
        />
      </Stack>
      {isRangeError && (
        <Typography color="error" variant="caption">
          {`Value must be a whole number between 0-${sliderProps.max || 50}`}
        </Typography>
      )}
    </FormControl>
  );
};
